import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input, Select, message, Drawer, Tooltip, Popconfirm, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ClockCircleOutlined, EyeOutlined,
  CheckCircleOutlined, RiseOutlined, FallOutlined, SearchOutlined,
} from '@ant-design/icons'
import { useMasterData, type Slot } from '@/context/MasterDataContext'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { generateCode } from '@/utils/codeGenerator'

const BASE = '/master/slots'

const CAT_PILL: Record<string, string> = {
  'off-peak': 'pill-info', 'normal': 'pill-green', 'peak': 'pill-danger',
}
const CAT_LABEL: Record<string, string> = {
  'off-peak': 'Off-Peak', 'normal': 'Normal', 'peak': 'Peak',
}

const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

const toMins = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0) }

const calcHours = (s: Slot): number | null => {
  if (!s.startTime || !s.endTime) return null
  const start = toMins(s.startTime)
  let end = toMins(s.endTime)
  if (end <= start) end += 1440
  let total = (end - start) / 60

  if (s.startTime2 && s.endTime2) {
    const start2 = toMins(s.startTime2)
    let end2 = toMins(s.endTime2)
    if (end2 <= start2) end2 += 1440
    total += (end2 - start2) / 60
  }
  return total
}

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 140, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const SlotMaster: React.FC = () => {
  const { slots, deleteSlot, saveSlot, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [liveHours, setLiveHours] = useState<number | null>(null)
  const [viewRecord, setViewRecord] = useState<Slot | null>(null)
  const [showSecondTime, setShowSecondTime] = useState(false)
  const [form]                  = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  /* List search / filter */
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  const peakCount   = slots.filter(s => s.category === 'peak').length
  const offPkCount  = slots.filter(s => s.category === 'off-peak').length
  const activeCount = slots.filter(s => s.status === 'active').length

  const filteredSlots = slots.filter(s => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      s.slotCode.toLowerCase().includes(q) ||
      (s.description || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || s.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
      setLiveHours(null)
      setShowSecondTime(false)
    } else if (mode === 'edit') {
      const rec = slots.find(s => s.id === editId)
      if (rec) {
        form.setFieldsValue(rec)
        setLiveHours(calcHours(rec))
        setShowSecondTime(!!(rec.startTime2 || rec.endTime2))
      } else {
        goList()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, slots])

  const handleDelete = async (id: string) => {
    try {
      await deleteSlot(id)
      message.success('Removed')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const onFinish = async (values: any) => {
    const finalValues = { ...values }
    if (!showSecondTime) {
      delete finalValues.startTime2
      delete finalValues.endTime2
    }
    const isEdit = mode === 'edit'
    let id = isEdit ? editId! : ''
    if (!isEdit) {
      try { id = await generateCode('slots') } catch { id = genId('S') }
    }
    const payload = { ...finalValues, id }

    setSubmitting(true)
    try {
      await saveSlot(payload, isEdit)
      message.success(isEdit ? 'Slot updated' : 'Slot added')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const columns = [
    {
      title: 'Slot Code', dataIndex: 'slotCode', width: 100,
      render: (v: string) => (
        <span style={{ ...MONO, fontWeight: 700, color: '#1677ff', fontSize: 13, cursor: 'pointer' }}>
          {v}
        </span>
      ),
    },
    {
      title: 'Time Range', key: 'time',
      render: (_: any, r: Slot) => (
        <div>
          <div style={{ ...MONO, fontWeight: 600, fontSize: 13 }}>{r.startTime} – {r.endTime}</div>
          {r.startTime2 && r.endTime2 && (
            <div style={{ ...MONO, fontSize: 12, color: '#8A93A6' }}>{r.startTime2} – {r.endTime2}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Hours', key: 'totalHours',
      render: (_: any, r: Slot) => {
        const hrs = calcHours(r)
        return hrs !== null
          ? <span style={{ ...MONO, fontWeight: 600 }}>{hrs.toFixed(1)} hrs</span>
          : '—'
      },
    },
    {
      title: 'Peak', dataIndex: 'category',
      render: (v: string) => <span className={`pill ${CAT_PILL[v] || 'pill-neutral'}`}>{CAT_LABEL[v] || v}</span>,
    },
    {
      title: 'Effective From', dataIndex: 'effectiveFrom',
      render: (v: string) => v ? <span style={{ ...MONO, fontSize: 12 }}>{v}</span> : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      title: 'Effective To', dataIndex: 'effectiveTo',
      render: (v: string) => v ? <span style={{ ...MONO, fontSize: 12 }}>{v}</span> : <span style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>Current</span>,
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (s: string) => <span className={`pill ${s === 'active' ? 'pill-green' : 'pill-neutral'}`}>{s === 'active' ? 'Active' : 'Inactive'}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Slot) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ──────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? 'Edit Slot' : 'Add Slot'}
        subtitle="Time-of-day slot code with its time block(s) and peak category"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ marginTop: 16 }}
          onValuesChange={(_changedValues, allValues) => {
            let total = null
            if (allValues.startTime && allValues.endTime) {
              const start = toMins(allValues.startTime)
              let end = toMins(allValues.endTime)
              if (end <= start) end += 1440
              total = (end - start) / 60
            }
            if (allValues.startTime2 && allValues.endTime2) {
              const start2 = toMins(allValues.startTime2)
              let end2 = toMins(allValues.endTime2)
              if (end2 <= start2) end2 += 1440
              total = (total || 0) + (end2 - start2) / 60
            }
            setLiveHours(total)
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="slotCode" label="Slot Code" rules={[{ required: true }]}>
              <Input placeholder="e.g. C1" style={{ ...MONO, fontWeight: 700, fontSize: 16 }} />
            </Form.Item>
            <Form.Item name="category" label="Peak Category" rules={[{ required: true }]}>
              <Select placeholder="Select peak type"
                options={Object.entries(CAT_LABEL).map(([k, v]) => ({ value: k, label: v }))} />
            </Form.Item>
            <Form.Item name="startTime" label="Start Time" rules={[{ required: true }]}>
              <Input type="time" style={MONO} />
            </Form.Item>
            <Form.Item name="endTime" label="End Time" rules={[{ required: true }]}>
              <Input type="time" style={MONO} />
            </Form.Item>
          </div>

          {!showSecondTime && (
            <div style={{ marginBottom: 16 }}>
              <Button type="dashed" size="small" onClick={() => setShowSecondTime(true)}>
                + Add Time
              </Button>
            </div>
          )}

          {showSecondTime && (
            <div style={{ padding: 12, background: '#F8FAFC', borderRadius: 8, marginBottom: 16, position: 'relative' }}>
              <Button type="text" danger size="small" style={{ position: 'absolute', top: 4, right: 4 }} onClick={() => {
                setShowSecondTime(false)
                form.setFieldsValue({ startTime2: undefined, endTime2: undefined })
                const vals = form.getFieldsValue()
                if (vals.startTime && vals.endTime) {
                  const start = toMins(vals.startTime)
                  let end = toMins(vals.endTime)
                  if (end <= start) end += 1440
                  setLiveHours((end - start) / 60)
                } else {
                  setLiveHours(null)
                }
              }}>Remove</Button>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px', marginTop: 12 }}>
                <Form.Item name="startTime2" label="Start Time">
                  <Input type="time" style={MONO} />
                </Form.Item>
                <Form.Item name="endTime2" label="End Time">
                  <Input type="time" style={MONO} />
                </Form.Item>
              </div>
            </div>
          )}

          {liveHours !== null && (
            <div style={{
              background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 8,
              padding: '8px 12px', marginBottom: 16, fontWeight: 600, color: '#166534',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span>Calculated Duration:</span>
              <span style={{ ...MONO, fontSize: 15, fontWeight: 700 }}>{liveHours.toFixed(1)} hours</span>
            </div>
          )}

          <Form.Item name="description" label="Description">
            <Input placeholder="e.g. Night / Off-peak hours" />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="effectiveFrom" label="Effective From" rules={[{ required: true, message: 'Effective From is required' }]}>
              <Input type="date" style={MONO} />
            </Form.Item>
            <Form.Item name="effectiveTo" label="Effective To">
              <Input type="date" style={MONO} placeholder="Leave blank = current" />
            </Form.Item>
          </div>
          <Form.Item name="status" label="Status" initialValue="active">
            <Select options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>{mode === 'edit' ? 'Update Slot' : 'Add Slot'}</Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={8} />

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ClockCircleOutlined style={{ color: '#001A4D' }} /> Slot Master
          </h2>
          <p className="page-subtitle">Time-of-day slot codes (C1, C2, C3…) — used by Meter Readings and Unit Allocation</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add Slot</Button>
      </div>

      <KpiCardRow
        cards={[
          { label: 'Total Slots', value: slots.length, icon: <ClockCircleOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Peak Slots', value: peakCount, icon: <RiseOutlined style={{ fontSize: 24, color: '#DC2626' }} />, iconBg: 'rgba(220,38,38,0.08)', borderColor: '#DC2626' },
          { label: 'Off-Peak', value: offPkCount, icon: <FallOutlined style={{ fontSize: 24, color: '#2563EB' }} />, iconBg: 'rgba(37,99,235,0.08)', borderColor: '#2563EB' },
        ]}
      />

      {/* 24-hour timeline */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6', marginBottom: 12 }}>
          24-Hour Slot Timeline
        </div>
        <div style={{ display: 'flex', height: 36, borderRadius: 8, overflow: 'hidden', border: '1px solid #E4E8F0' }}>
          {slots.filter(s => s.status === 'active').flatMap(s => {
            const blocks = [{ start: s.startTime, end: s.endTime }]
            if (s.startTime2 && s.endTime2) blocks.push({ start: s.startTime2, end: s.endTime2 })
            return blocks.map(b => ({ ...s, blockStart: b.start, blockEnd: b.end }))
          }).sort((a, b) => toMins(a.blockStart) - toMins(b.blockStart)).map((s, i, arr) => {
            const start = toMins(s.blockStart)
            const end   = s.blockEnd === '00:00' ? 1440 : toMins(s.blockEnd)
            const width = ((end - start) / 1440) * 100
            const bg:  Record<string, string> = { peak: '#FEE2E2', normal: '#D1FAE5', 'off-peak': '#DBEAFE' }
            const clr: Record<string, string> = { peak: '#DC2626', normal: '#059669', 'off-peak': '#2563EB' }
            return (
              <div key={`${s.id}-${i}`} title={`${s.slotCode}: ${s.blockStart}–${s.blockEnd}`}
                style={{ width: `${width}%`, background: bg[s.category] || '#F1F4F9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRight: i < arr.length - 1 ? '1px solid #E4E8F0' : 'none' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: clr[s.category] || '#4A5568' }}>{s.slotCode}</span>
              </div>
            )
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: '#8A93A6', ...MONO }}>
          <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>24:00</span>
        </div>
      </Card>

      {/* ── Filter bar ── */}
      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by slot code or description..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredSlots}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} records` }}
        />
      </Card>

      {/* ── Detail Drawer ── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, ...MONO }}>{viewRecord?.slotCode}</div>
            <div style={{ fontSize: 12, color: '#8A93A6' }}>
              {viewRecord?.startTime} – {viewRecord?.endTime}
              {viewRecord?.startTime2 && viewRecord?.endTime2 ? ` | ${viewRecord?.startTime2} – ${viewRecord?.endTime2}` : ''}
            </div>
          </div>
        }
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        width={400}
        extra={
          <Space>
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined />} onClick={() => { if (viewRecord) goEdit(viewRecord.id); setViewRecord(null) }} />
            </Tooltip>
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this record?"
                description="This action cannot be undone."
                onConfirm={() => { handleDelete(viewRecord!.id); setViewRecord(null) }}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </Space>
        }
      >
        {viewRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <span className={`pill ${CAT_PILL[viewRecord.category] || 'pill-neutral'}`}>{CAT_LABEL[viewRecord.category] || viewRecord.category}</span>
              <span style={{ marginLeft: 8 }} className={`pill ${viewRecord.status === 'active' ? 'pill-green' : 'pill-neutral'}`}>{viewRecord.status === 'active' ? 'Active' : 'Inactive'}</span>
            </div>
            <DetailRow label="Slot Code" value={<span style={{ ...MONO, fontSize: 18, fontWeight: 700, color: '#001A4D' }}>{viewRecord.slotCode}</span>} />
            <DetailRow label="Time Block 1" value={<span style={MONO}>{viewRecord.startTime} – {viewRecord.endTime}</span>} />
            {(viewRecord.startTime2 || viewRecord.endTime2) && (
              <DetailRow label="Time Block 2" value={<span style={MONO}>{viewRecord.startTime2 || '—'} – {viewRecord.endTime2 || '—'}</span>} />
            )}
            <DetailRow label="Duration" value={(() => {
              const hrs = calcHours(viewRecord)
              return hrs !== null ? <span style={{ ...MONO, fontWeight: 700, color: '#059669' }}>{hrs.toFixed(1)} hours</span> : undefined
            })()} />
            <DetailRow label="Effective From" value={viewRecord.effectiveFrom ? <span style={MONO}>{viewRecord.effectiveFrom}</span> : undefined} />
            <DetailRow label="Effective To" value={viewRecord.effectiveTo ? <span style={MONO}>{viewRecord.effectiveTo}</span> : <span style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>Current</span>} />
            <DetailRow label="Description" value={viewRecord.description} />
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default SlotMaster
